import {
  Button,
  FormControl,
  FormLabel,
  Grid,
  Paper,
  Stack,
  TableBody,
  TableContainer,
} from '@mui/material';
import {
  defaultTableAssistanceRowAEchoir,
  defaultTableAssistanceRowEchu,
} from '@norma/shared/constants/prejudice/defaultValues/FormCalendrierAssistance.defaultValues';
import { MonetaryErosion } from '@norma/shared/types/monetaryErosion.type';
import {
  NumeroPieceValuesRow,
  PrejudiceFormCalendrierAssistance,
  PrejudiceFormCalendrierAssistanceCapitalisation,
  PrejudiceFormCalendrierAssistanceRow,
  PrejudiceType,
} from '@norma/shared/types/prejudice.type';
import {
  Procedure,
  ProcedureHospitalisation,
} from '@norma/shared/types/procedure.type';
import { Victime } from '@norma/shared/types/victime.type';
import React from 'react';
import { Control, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CalculsBox } from 'src/components/basic/CalculsBox';
import { RadioFormField } from 'src/components/forms/RadioFieldForm';
import { TextFieldForm } from 'src/components/forms/TextFieldForm';
import { IMaskNumberProps, MaskNumber } from 'src/components/masks/MaskNumber';
import { useFormArrayUtils } from 'src/hooks/useFormArrayUtils';
import { ComputedPropsForm } from '../../../forms/ComputedPropsForm';
import { NormalTable } from '../../../styled';
import { TableAssistanceFooter } from './TableAssistanceFooter';
import { TableAssistanceHeader } from './TableAssistanceHeader';
import { TableAssistanceRow } from './TableAssistanceRow';
interface Props {
  control:
    | Control<PrejudiceFormCalendrierAssistance>
    | Control<PrejudiceFormCalendrierAssistanceCapitalisation>;
  tiersPayeurs: string[];
  hospitalisations: ProcedureHospitalisation[];
  monetaryErosions: MonetaryErosion[];
  dateConsolidation: Date | undefined | null;
  dateLiquidation?: Date | undefined;
  isCalculCapitalisation?: boolean;
  prejudiceType: PrejudiceType;
  procedure: Procedure;
  victime: Victime;
  DFTPRows?: PrejudiceFormCalendrierAssistanceRow[] | null;
  allNumerosPieces: NumeroPieceValuesRow[];
  shouldNotDisplayCapitalisation: boolean;
  dateDeces: Date | undefined;
}

export const TableAssistance: React.FC<Props> = ({
  control,
  tiersPayeurs,
  dateLiquidation,
  isCalculCapitalisation,
  DFTPRows,
  shouldNotDisplayCapitalisation,
  dateDeces,
  ...props
}) => {
  const { t } = useTranslation();
  const { getValues, setValue } =
    useFormContext<PrejudiceFormCalendrierAssistance>();
  const { fields, duplicate, remove, append } = useFormArrayUtils({
    control: control as Control<PrejudiceFormCalendrierAssistance>,
    name: 'rows',
    getValues,
    setValue,
  });
  const handleAppendDFTPRows = () => {
    if (DFTPRows) {
      append(DFTPRows);
      setValue?.('isDFTPRowsAdded', true);
    }
  };

  return (
    <>
      <Stack
        alignItems="start"
        width="100%"
        direction="row"
        spacing={2}
        justifyContent="space-between"
      >
        <CalculsBox padding={2}>
          <FormControl>
            <FormLabel
              sx={{
                textAlign: 'center',
              }}
            >
              {t('prejudice.revalorisation.coefficientsType.choice.label')}
            </FormLabel>
            <RadioFormField
              control={control as Control<PrejudiceFormCalendrierAssistance>}
              name="revalorisationCoefficientsType"
              options={[
                {
                  label: t(
                    'prejudice.revalorisation.coefficientsType.choice.options.smic',
                  ),
                  value: 'smic',
                },
                {
                  label: t(
                    'prejudice.revalorisation.coefficientsType.choice.options.annuel',
                  ),
                  value: 'annuel',
                },
              ]}
              row={true}
            />
          </FormControl>
        </CalculsBox>
        <TextFieldForm
          sx={{ alignSelf: 'end', maxWidth: '200px' }}
          control={control as unknown as Control<FieldValues>}
          name="forfaitHoraire"
          label="Forfait horaire"
          InputLabelProps={{
            shrink: true,
            sx: { color: 'info.light' },
          }}
          InputProps={{
            inputComponent: MaskNumber as any,
            inputProps: {
              numberMask: { scale: 1 },
              suffix: '€',
            } as IMaskNumberProps,
            sx: {
              color: 'info.light',
              borderColor: 'info.light',
              ' .MuiOutlinedInput-notchedOutline': {
                borderColor: 'info.light',
              },
            },
          }}
          multiline
          maxRows={4}
          fullWidth
        />
      </Stack>

      <TableContainer component={Paper}>
        <NormalTable size="medium" stickyHeader>
          <TableAssistanceHeader
            tiersPayeurs={tiersPayeurs}
            isCalculCapitalisation={isCalculCapitalisation}
            shouldNotDisplayCapitalisation={shouldNotDisplayCapitalisation}
          />
          <TableBody>
            {fields.map((field, index) => (
              <TableAssistanceRow
                key={field.id}
                field={field}
                index={index}
                control={control}
                tiersPayeurs={tiersPayeurs}
                dateLiquidation={dateLiquidation}
                isCalculCapitalisation={isCalculCapitalisation}
                onClickRemove={remove}
                onClickDuplicate={duplicate}
                shouldNotDisplayCapitalisation={shouldNotDisplayCapitalisation}
                dateDeces={dateDeces}
                {...props}
              />
            ))}
          </TableBody>
          <TableAssistanceFooter
            control={control}
            tiersPayeurs={tiersPayeurs}
            partResponsabilite={props.procedure.partResponsabilite}
            isCalculCapitalisation={isCalculCapitalisation}
            monetaryErosions={props.monetaryErosions}
            dateLiquidation={dateLiquidation}
            shouldNotDisplayCapitalisation={shouldNotDisplayCapitalisation}
          />
        </NormalTable>
      </TableContainer>
      <Grid container spacing={2}>
        {!isCalculCapitalisation && <Grid item xs={3} />}
        <Grid
          item
          xs={isCalculCapitalisation ? 12 : 6}
          display="flex"
          justifyContent="center"
          gap={2}
        >
          <Button
            onClick={() => {
              const newRow: PrejudiceFormCalendrierAssistanceRow =
                defaultTableAssistanceRowEchu({
                  procedure: props.procedure,
                  isCalculCapitalisation: Boolean(isCalculCapitalisation),
                  existingValues: getValues(),
                  existingRow: undefined,
                });

              append({
                ...newRow,
                dateDebut:
                  getValues().rows[getValues().rows.length - 1]?.dateFin ??
                  newRow.dateDebut ??
                  null,
              });
            }}
          >
            {isCalculCapitalisation && !shouldNotDisplayCapitalisation
              ? t(
                  'prejudice.prejudicesFormTypes.CALENDRIER_ASSISTANCE.fields.rows.type.options.echu',
                )
              : t('forms.table.addRow')}
          </Button>
          {isCalculCapitalisation && !shouldNotDisplayCapitalisation ? (
            <Button
              onClick={() => {
                append(
                  defaultTableAssistanceRowAEchoir({
                    procedure: props.procedure,
                    existingValues: getValues(),
                    existingRow: undefined,
                  }),
                );
              }}
            >
              {t(
                'prejudice.prejudicesFormTypes.CALENDRIER_ASSISTANCE.fields.rows.type.options.aEchoir',
              )}
            </Button>
          ) : null}
        </Grid>
        <Grid item xs={3} display="flex" justifyContent="center">
          {!isCalculCapitalisation && (
            <ComputedPropsForm
              control={control as Control<PrejudiceFormCalendrierAssistance>}
              watchFields={['isDFTPRowsAdded']}
              compute={([isDFTPRowsAdded]) => {
                return {
                  hidden: isDFTPRowsAdded || !DFTPRows || DFTPRows.length === 0,
                  props: {},
                };
              }}
              render={() => (
                <Button onClick={handleAppendDFTPRows}>
                  {t(
                    'prejudice.prejudicesFormTypes.CALENDRIER_ASSISTANCE.addDFTPRows.buttonText',
                  )}
                </Button>
              )}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};
